import React, { useState, useEffect, useCallback } from "react";
import {
  CustomerField,
  FieldTypeChoice,
  useUpdateCustomerFieldMutation,
  useDeleteCustomerFieldMutation,
} from "../../api/customerFieldApi";
import { ProcessorSpecExpander } from "./ProcessorSpecExpander";
import { ListValuesEditor } from "./ListValuesEditor";
import { Button } from "../../components/tailwind/button/Button";
import { Dropdown } from "../../components/tailwind/dropdown/Dropdown";
import debounce from "lodash/debounce";

const LIST_FIELD_TYPES = ["SINGLE_SELECT_LIST", "MULTI_SELECT_LIST"];

interface Props {
  field: CustomerField;
  fieldTypeChoices: FieldTypeChoice[];
}

export const CustomerFieldCard: React.FC<Props> = ({
  field,
  fieldTypeChoices,
}) => {
  const [updateField] = useUpdateCustomerFieldMutation();
  const [
    deleteField,
    { isLoading: isDeleting },
  ] = useDeleteCustomerFieldMutation();

  const [name, setName] = useState(field.name || "");
  const [type, setType] = useState(field.type || "");
  const [entityId, setEntityId] = useState(field.entity_id || "");
  const [description, setDescription] = useState(field.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isListValuesExpanded, setIsListValuesExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isListType = type && LIST_FIELD_TYPES.includes(type);
  const listValuesCount = field.list_values?.length || 0;

  // Reset local state when field changes
  useEffect(() => {
    setName(field.name || "");
    setType(field.type || "");
    setEntityId(field.entity_id || "");
    setDescription(field.description || "");
  }, [field.name, field.type, field.description]);

  // Debounced save function
  const debouncedSave = useCallback(
    debounce(
      (updates: {
        name?: string;
        type?: string | null;
        entity_id?: string | null;
        description?: string;
      }) => {
        updateField({ id: field.id, ...updates });
      },
      1000
    ),
    [field.id, updateField]
  );

  const handleNameChange = (value: string): void => {
    setName(value);
    debouncedSave({ name: value });
  };

  const handleTypeChange = (value: string): void => {
    setType(value);
    updateField({ id: field.id, type: value || null });
  };

  const handleEntityIdChange = (value: string): void => {
    setEntityId(value);
    updateField({ id: field.id, entity_id: value || null });
  };

  const handleDescriptionChange = (value: string): void => {
    setDescription(value);
    debouncedSave({ description: value });
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await deleteField(field.id).unwrap();
    } catch (err) {
      console.error("Failed to delete field:", err);
    }
  };

  const typeOptions = fieldTypeChoices.map((choice) => ({
    key: choice.value,
    value: choice.value,
    text: choice.label,
  }));

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden tw-w-full">
      {/* Row 1: Field ID */}
      <div className="tw-px-5 tw-pt-4 tw-pb-2">
        <div className="tw-bg-gray-100 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-font-mono tw-text-gray-700 tw-inline-block">
          {field.field_id}
        </div>
      </div>

      {/* Row 2: Name, Entity ID, Type, Delete */}
      <div className="tw-px-5 tw-pb-3 tw-flex tw-items-end tw-gap-4">
        {/* Name */}
        <div className="tw-flex-1 tw-min-w-0">
          <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Enter name..."
            className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-py-2"
          />
        </div>

        {/* Entity ID */}
        <div className="tw-flex-1 tw-min-w-0">
          <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
            Entity ID
          </label>
          <input
            type="text"
            value={entityId}
            onChange={(e) => handleEntityIdChange(e.target.value)}
            placeholder="Enter entity ID..."
            className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-py-2"
          />
        </div>

        {/* Type */}
        <div className="tw-w-56 tw-flex-shrink-0">
          <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
            Type
          </label>
          <Dropdown
            options={typeOptions}
            value={type}
            onChange={(value) => handleTypeChange(value as string)}
            placeholder="Select type..."
            clearable
          />
        </div>

        {/* Delete button */}
        <div className="tw-flex-shrink-0">
          {showDeleteConfirm ? (
            <div className="tw-flex tw-gap-2">
              <Button
                variant="primary-alt"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "..." : "Confirm"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="tw-p-1.5 tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors"
              title="Delete field"
            >
              <i className="material-icons tw-text-xl">delete</i>
            </button>
          )}
        </div>
      </div>

      {/* Row 3: Description */}
      <div className="tw-px-5 tw-pb-3">
        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
          Description
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder="Enter description..."
          className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-py-2"
        />
      </div>

      {/* Row 4: List Values toggle (only for list types) */}
      {isListType && (
        <>
          <div className="tw-border-t tw-border-gray-100">
            <button
              onClick={() => setIsListValuesExpanded(!isListValuesExpanded)}
              className="tw-w-full tw-px-5 tw-py-3 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
            >
              <i className="material-icons tw-text-lg tw-text-gray-500">list</i>
              <span>List Values</span>
              {listValuesCount > 0 && (
                <span className="tw-bg-purple-100 tw-text-purple-700 tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">
                  {listValuesCount}
                </span>
              )}
              <i
                className={`material-icons tw-text-lg tw-text-gray-400 tw-ml-auto tw-transition-transform ${
                  isListValuesExpanded ? "tw-rotate-180" : ""
                }`}
              >
                expand_more
              </i>
            </button>
          </div>

          {/* List Values Expander Content */}
          {isListValuesExpanded && (
            <div className="tw-border-t tw-border-gray-200 tw-bg-gray-50">
              <ListValuesEditor
                fieldId={field.id}
                listValues={field.list_values}
              />
            </div>
          )}
        </>
      )}

      {/* Row 5: Processors toggle */}
      <div className="tw-border-t tw-border-gray-100">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="tw-w-full tw-px-5 tw-py-3 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
        >
          <i className="material-icons tw-text-lg tw-text-gray-500">settings</i>
          <span>Processors</span>
          {field.processor_specs_count > 0 && (
            <span className="tw-bg-blue-100 tw-text-blue-700 tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">
              {field.processor_specs_count}
            </span>
          )}
          <i
            className={`material-icons tw-text-lg tw-text-gray-400 tw-ml-auto tw-transition-transform ${
              isExpanded ? "tw-rotate-180" : ""
            }`}
          >
            expand_more
          </i>
        </button>
      </div>

      {/* Processors Expander Content */}
      {isExpanded && (
        <div className="tw-border-t tw-border-gray-200 tw-bg-gray-50">
          <ProcessorSpecExpander customerFieldId={field.id} />
        </div>
      )}
    </div>
  );
};
